import * as React from 'react';

import {copyToClipboard} from 'nutshell-core/utils/copy-to-clipboard';

import {ActionableHeader} from 'shells/actionable-header/actionable-header';
import {Button} from 'shells/button/button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {EditionThumbnailList} from 'shells/edition-thumbnail-list/edition-thumbnail-list';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';
// @ts-expect-error - imported from .js/.jsx file
import {ArchiveIcon} from 'shells/icon/svg/archive-icon';
// @ts-expect-error - imported from .js/.jsx file
import {CloneIcon} from 'shells/icon/svg/clone-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ExternalLinkIcon} from 'shells/icon/svg/external-link-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
import {Illustration} from 'shells/illustrations/illustration';
import {Link} from 'shells/link/link';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {
    NewsletterStubFragment as Newsletter,
    EditionStubFragment as Edition,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {NewNewsletterModal} from './new-newsletter-modal';

import styles from './newsletter-list-item.module.css';

type Props = {
    newsletter: Newsletter;
    editions: Edition[];
    onDeleteNewsletter?: (newsletterId: string) => Promise<any>;
    onRestoreNewsletter?: (newsletterId: string) => Promise<any>;
    onCreateEdition?: (name: string, campaignId: string) => Promise<any>;
    onDeleteEdition: (editionId: string, newsletterId: string) => Promise<any>;
    onDuplicateEdition: (
        editionId: string,
        campaignId: string,
        name?: string | null | undefined
    ) => Promise<any>;
    isLoadingEditions: boolean;
    isArchived: boolean;
    editionsError: string | null | undefined;
};

export function NewsletterListItem(props: Props) {
    const [duplicatingEdition, setDuplicatingEdition] = React.useState<any | null | undefined>(
        null
    );
    const [isDuplicatingEdition, setIsDuplicatingEdition] = React.useState<boolean>(false);
    const [isAddingNewEdition, setIsAddingNewEdition] = React.useState<boolean>(false);

    const options = [
        {
            label: 'View',
            handleSelect: () => {
                appHistory.push(props.newsletter.htmlUrlPath);
            },
            icon: <EyeIcon wrapWithDiv={false} size={18} />,
        },
    ];

    const onDeleteNewsletter = props.onDeleteNewsletter;
    const onRestoreNewsletter = props.onRestoreNewsletter;

    if (onDeleteNewsletter) {
        options.push({
            label: 'Archive',
            // @ts-expect-error
            variant: 'destructive',
            handleSelect: () => {
                onDeleteNewsletter(props.newsletter.id);
            },
            icon: <ArchiveIcon size={16} />,
        });
    }

    if (onRestoreNewsletter) {
        options.push({
            label: 'Restore',
            handleSelect: () => {
                onRestoreNewsletter(props.newsletter.id);
            },
            icon: <ArchiveIcon size={16} />,
        });
    }

    return (
        <div className={styles['newsletter-list-item']}>
            <ActionableHeader
                isArchived={props.isArchived}
                name={props.newsletter.name}
                to={props.newsletter.htmlUrlPath}
                metadataPrimary={
                    props.newsletter.editions.totalCount === 1
                        ? `${props.newsletter.editions.totalCount} edition`
                        : `${props.newsletter.editions.totalCount} editions`
                }
                secondaryLink={{
                    text: 'Engagement',
                    onClick: () => {
                        appHistory.push(`${props.newsletter.htmlUrlPath}/engagement`);
                    },
                }}
                options={options}
                noPadding={true}
            />
            {props.editionsError ? (
                <EmptyStateSmall
                    title={props.editionsError}
                    subtitle={
                        <div className='flex'>
                            Please&nbsp;
                            <ButtonMover size='xlarge' direction={['left', 'right', 'up', 'down']}>
                                <Button
                                    size='xlarge'
                                    variant='text-primary'
                                    onClick={() => {
                                        location.reload();
                                    }}
                                >
                                    try again
                                </Button>
                            </ButtonMover>
                            &nbsp;or&nbsp;
                            <Link href='https://www.nutshell.com/support' variant='primary'>
                                contact support.
                            </Link>
                        </div>
                    }
                    icon={
                        <div style={{width: '150px'}}>
                            <Illustration src='plug-error-state' width='100%' height='100%' />
                        </div>
                    }
                />
            ) : (
                <div className={styles['edition-list']}>
                    <EditionThumbnailList
                        isLoading={props.isLoadingEditions}
                        // @ts-expect-error
                        editions={props.editions.map((edition) => {
                            const editionOptions = [];

                            const editionToDisplay =
                                edition.comparison && edition.comparison.winnerEdition
                                    ? edition.comparison.winnerEdition
                                    : edition;

                            editionOptions.push({
                                label: 'View',
                                handleSelect: () => {
                                    // edition is the A edition which is the one we want to link to
                                    appHistory.push(edition.htmlUrlPath);
                                },
                                icon: <EyeIcon wrapWithDiv={false} size={18} />,
                            });

                            editionOptions.push({
                                label: 'Make a copy',
                                handleSelect: () => {
                                    setDuplicatingEdition(editionToDisplay);
                                },
                                icon: <CloneIcon size={16} />,
                            });

                            editionOptions.push({
                                label: 'Copy shareable URL',
                                handleSelect: () => {
                                    copyToClipboard(editionToDisplay.viewInBrowserLink);
                                },
                                tooltipTextOnClose: 'Copied!',
                                icon: <ExternalLinkIcon size={16} />,
                            });

                            if (edition.status === 'NEW') {
                                editionOptions.push({
                                    label: 'Archive',
                                    variant: 'destructive',
                                    handleSelect: () => {
                                        // edition is the A edition which is the one we want to delete
                                        props.onDeleteEdition(edition.id, props.newsletter.id);
                                    },
                                    icon: <ArchiveIcon size={16} />,
                                });
                            }

                            return {
                                ...edition,
                                status: props.isArchived ? 'ARCHIVED' : edition.status,
                                thumbnailUrl:
                                    editionToDisplay.emailTemplate &&
                                    editionToDisplay.emailTemplate.thumbnailUrl
                                        ? editionToDisplay.emailTemplate.thumbnailUrl
                                        : undefined,
                                options: editionOptions,
                                campaignType: edition.campaign.type,
                            };
                        })}
                        addNew={
                            props.onCreateEdition
                                ? {
                                      onClick: () => {
                                          setIsAddingNewEdition(true);
                                      },
                                      text: 'Create new edition',
                                  }
                                : undefined
                        }
                        onSelect={(editionId: string) => {
                            const editionEdge = props.editions.find((edition) => {
                                return edition.id === editionId;
                            });

                            if (editionEdge) {
                                appHistory.push(editionEdge.htmlUrlPath);
                            }
                        }}
                        seeAll={{
                            onClick: () => {
                                appHistory.push(props.newsletter.htmlUrlPath);
                            },
                            text: 'See all editions',
                        }}
                        singleRow={true}
                        loadingStateBackgroundColor='off-white'
                    />
                </div>
            )}
            {duplicatingEdition ? (
                <SingleInputModal
                    requireValue={true}
                    buttonText='Create'
                    initialValue={`Copy of ${duplicatingEdition.name}`}
                    title='Name your new edition'
                    subtitle='Add a descriptive name so you can find it later'
                    placeholder={`Copy of ${duplicatingEdition.name}`}
                    isSaving={isDuplicatingEdition}
                    onClose={() => {
                        setDuplicatingEdition(null);
                    }}
                    onSave={(name) => {
                        setIsDuplicatingEdition(true);
                        const nameValue = name && name.length ? name : null;
                        props
                            .onDuplicateEdition(
                                duplicatingEdition.id,
                                props.newsletter.id,
                                nameValue
                            )
                            .then(() => {
                                setIsDuplicatingEdition(false);
                                setDuplicatingEdition(null);
                            });
                    }}
                />
            ) : undefined}
            {isAddingNewEdition ? (
                <NewNewsletterModal
                    campaignId={props.newsletter.id}
                    isOpen={true}
                    onClose={() => {
                        setIsAddingNewEdition(false);
                    }}
                    // @ts-expect-error
                    onCreateEdition={
                        props.onCreateEdition
                            ? props.onCreateEdition
                            : () => {
                                  return new Promise(() => {});
                              }
                    }
                    onDuplicateEdition={props.onDuplicateEdition}
                />
            ) : undefined}
        </div>
    );
}
